// Client for the calculator backend's /artworks endpoint.
//
//   import { ArtworksApi } from '@theme/artworks-api';
//
//   const api = new ArtworksApi({ customerId: '1234567890' });
//   const artworks = await api.list({ signal });                     // GET
//   const fields = await api.uploadFile(file);                       // mint + upload
//   const saved = await api.create(fields);                          // POST
//
//   `uploadFile` uploads to Google Cloud Storage. `upscaleFile` stages a file in
//   Cloudinary to run its generative upscaler and returns the result as a File,
//   ready to hand to `uploadFile` — so stored artwork is always a GCS URL.

// const ARTWORKS_BASE_URL = "https://stickerbros-calculator--development.gadget.app";
const ARTWORKS_BASE_URL = 'https://stickerbros-calculator--sticky-deals.gadget.app';

// The Cloudinary account serving both the preview transform and the upscale
// staging upload below.
// const CLOUDINARY_CLOUD_NAME = "ksjh2r7y";
const CLOUDINARY_CLOUD_NAME = "uuzdvmpw";

// Cloudinary fetch transform — prepended to a stored file URL to produce an
// optimized, resized preview (GCS has no transform layer of its own).
export const CLOUDINARY_FETCH_PREFIX = `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/fetch/c_lfill,fl_lossy,f_auto,q_auto:best,w_auto/`;

// Cloudinary's generative upscaler (e_upscale) is not supported for fetched
// images, so an upscale means a real upload to Cloudinary rather than a
// transform of the GCS URL. See ArtworksApi#upscaleFile.
const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;

// Logged-out visitors have no customerId, so their uploads are kept in
// sessionStorage instead of being persisted to / read from our DB.
const GUEST_ARTWORKS_KEY = "stickerbros:guest-artworks";

/**
 * @typedef {object} Artwork
 * @property {string} id
 * @property {string} previewUrl
 * @property {string} fileUrl
 * @property {string} [name]
 * @property {number | null} [width] - Pixel width, recorded at upload. Absent on rows created before dimensions were tracked.
 * @property {number | null} [height] - Pixel height, same caveat.
 * @property {string | null} [mimeType] - The browser-reported type at upload, e.g. "image/png".
 * @property {string | null} [extension] - Lowercased file extension, e.g. "png". Survives a rename, unlike `name`.
 */

/**
 * @typedef {object} PageInfo
 * @property {boolean} hasNextPage
 * @property {boolean} hasPreviousPage
 * @property {string | null} startCursor
 * @property {string | null} endCursor
 */

export class ArtworksApi {
  /** @type {string} */
  #baseUrl;
  /** @type {string} */
  #shop;
  /** @type {string | null} */
  #customerId;

  /**
   * @param {object} [config]
   * @param {string | null} [config.customerId]
   * @param {string} [config.shop] - Defaults to the current Shopify shop domain.
   * @param {string} [config.baseUrl]
   */
  constructor({ customerId = null, shop, baseUrl = ARTWORKS_BASE_URL } = {}) {
    this.#customerId = customerId;
    this.#shop = shop ?? window.Shopify?.shop ?? "";
    this.#baseUrl = baseUrl;
  }

  /**
   * Builds an /artworks URL with the shop param and any extra query params.
   *
   * @param {string} [path] - Sub-path appended after `/artworks`, e.g. '/token'.
   * @param {Record<string, string | null>} [params]
   * @returns {URL}
   */
  #url(path = "", params = {}) {
    const url = new URL(`${this.#baseUrl}/artworks${path}`);
    url.searchParams.set("shop", this.#shop);
    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, value ?? "");
    }
    return url;
  }

  /**
   * Fetches a page of the customer's artworks (GET). Guests are served (and
   * searched) from sessionStorage with no real pagination.
   *
   * @param {object} [options]
   * @param {string} [options.search]
   * @param {number} [options.first] - Page size.
   * @param {string | null} [options.after] - Cursor to page after.
   * @param {AbortSignal} [options.signal]
   * @returns {Promise<{ artworks: Artwork[], pageInfo: PageInfo | null }>}
   */
  async list({ search = "", first, after = null, signal } = {}) {
    if (!this.#customerId) {
      const term = search.trim().toLowerCase();
      const all = readGuestArtworks();
      const artworks = term
        ? all.filter(
            (a) =>
              (a.name ?? "").toLowerCase().includes(term) ||
              a.id.toLowerCase().includes(term),
          )
        : all;
      return { artworks, pageInfo: null };
    }

    const params = { customerId: this.#customerId };
    if (search) params.search = search;
    if (first) params.first = String(first);
    if (after) params.after = after;

    const response = await fetch(this.#url("", params), { signal });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const json = await response.json();
    const artworks = Array.isArray(json) ? json : (json.data ?? []);
    return { artworks, pageInfo: json.pageInfo ?? null };
  }

  /**
   * Creates a new artwork record for the customer (POST).
   *
   * @param {object} artwork
   * @param {string} artwork.fileUrl
   * @param {string} artwork.previewUrl
   * @param {string} [artwork.name]
   * @param {number} [artwork.width] - Pixel width; lets later size changes be re-checked without re-measuring.
   * @param {number} [artwork.height]
   * @param {string} [artwork.mimeType] - Browser-reported type, e.g. "image/png".
   * @param {string} [artwork.extension] - Lowercased extension, e.g. "png".
   * @param {AbortSignal} [artwork.signal]
   * @returns {Promise<Artwork>}
   */
  async create({ fileUrl, previewUrl, name, width, height, mimeType, extension, signal } = {}) {
    if (!this.#customerId) {
      const artwork = {
        id: crypto.randomUUID?.() ?? String(Date.now()),
        fileUrl,
        previewUrl,
        name,
        width,
        height,
        mimeType,
        extension,
      };
      // Newest first, matching how a fresh upload should surface in the gallery.
      writeGuestArtworks([artwork, ...readGuestArtworks()]);
      return artwork;
    }

    const response = await fetch(this.#url(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        customerId: this.#customerId,
        fileUrl,
        previewUrl,
        name,
        width,
        height,
        mimeType,
        extension,
      }),
      signal,
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    return response.json();
  }

  /**
   * Renames an artwork (PUT /artworks). The id travels in the body.
   *
   * @param {object} params
   * @param {string} params.id
   * @param {string} params.name
   * @param {AbortSignal} [params.signal]
   * @returns {Promise<Artwork>}
   */
  async rename({ id, name, signal } = {}) {
    if (!this.#customerId) {
      const artworks = readGuestArtworks().map((a) =>
        a.id === id ? { ...a, name } : a,
      );
      writeGuestArtworks(artworks);
      return artworks.find((a) => a.id === id) ?? { id, name };
    }

    const response = await fetch(this.#url(), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, customerId: this.#customerId, name }),
      signal,
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    return response.json();
  }

  /**
   * Requests a signed GCS upload URL for a file (GET /artworks/signature/gcs).
   *
   * @param {object} params
   * @param {string} params.fileName
   * @param {string} params.contentType
   * @param {AbortSignal} [params.signal]
   * @returns {Promise<{ uploadUrl: string, fileUrl: string, objectName: string, contentType: string, success: boolean }>}
   */
  async generateGcsUploadUrl({ fileName, contentType, signal } = {}) {
    const response = await fetch(
      this.#url("/signature/gcs", {
        customerId: this.#customerId,
        fileName,
        contentType,
      }),
      { signal },
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const json = await response.json();
    if (!json.success) throw new Error("Failed to create upload URL");

    return json;
  }

  /**
   * Uploads a file to Google Cloud Storage and returns the fields needed by
   * {@link ArtworksApi#create}.
   *
   * @param {File} file
   * @param {object} [options]
   * @param {AbortSignal} [options.signal]
   * @returns {Promise<{ fileUrl: string, previewUrl: string, name: string }>}
   */
  async uploadFile(file, { signal } = {}) {
    const gcs = await this.generateGcsUploadUrl({
      fileName: file.name,
      contentType: file.type,
      signal,
    });
    await uploadToGcs(file, gcs, { signal });
    // Preview the GCS file through Cloudinary's fetch transform.
    return {
      fileUrl: gcs.fileUrl,
      previewUrl: `${CLOUDINARY_FETCH_PREFIX}${gcs.fileUrl}`,
      name: file.name,
    };
  }

  /**
   * Requests a backend-signed Cloudinary upload (GET /artworks/signature/cloudinary).
   *
   * The backend builds and signs the `eager` transformation itself — and clamps
   * the target size — so the theme can neither hold the API secret nor forge an
   * expensive transformation. Everything in the returned `params` must be POSTed
   * verbatim or the signature won't match.
   *
   * @param {object} params
   * @param {string} params.fileName
   * @param {number} params.targetWidth
   * @param {number} params.targetHeight
   * @param {AbortSignal} [params.signal]
   * @returns {Promise<{ cloudName: string, apiKey: string, signature: string, params: Record<string, string>, success: boolean }>}
   */
  async generateCloudinarySignature({
    fileName,
    targetWidth,
    targetHeight,
    signal,
  } = {}) {
    const response = await fetch(
      this.#url("/signature/cloudinary", {
        customerId: this.#customerId,
        fileName,
        targetWidth: String(targetWidth),
        targetHeight: String(targetHeight),
      }),
      { signal },
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const json = await response.json();
    if (!json.success) throw new Error("Failed to sign Cloudinary upload");

    return json;
  }

  /**
   * Runs a low-resolution raster through Cloudinary's generative upscaler and
   * hands back the result as a File.
   *
   * The Cloudinary asset is only a staging step: the returned File is meant to
   * go straight through {@link ArtworksApi#uploadFile}, so the print file still
   * ends up in GCS and our DB only ever stores GCS URLs.
   *
   * @param {File} file
   * @param {object} params
   * @param {number} params.targetWidth
   * @param {number} params.targetHeight
   * @param {AbortSignal} [params.signal]
   * @returns {Promise<File>}
   */
  async upscaleFile(file, { targetWidth, targetHeight, signal } = {}) {
    const { apiKey, signature, params } =
      await this.generateCloudinarySignature({
        fileName: file.name,
        targetWidth,
        targetHeight,
        signal,
      });

    const body = new FormData();
    body.set("file", file);
    body.set("api_key", apiKey);
    body.set("signature", signature);
    for (const [key, value] of Object.entries(params ?? {})) {
      body.set(key, String(value));
    }

    const response = await fetch(CLOUDINARY_UPLOAD_URL, {
      method: "POST",
      body,
      signal,
    });

    if (!response.ok) throw new Error(`Cloudinary HTTP ${response.status}`);

    const json = await response.json();
    // With eager_async off the derivative is normally ready in the response;
    // fall back to the derived URL (and its 423 retry) when it isn't.
    const url =
      json.eager?.[0]?.secure_url ??
      derivedUrl({ transformation: params?.eager, upload: json });
    if (!url) throw new Error("Cloudinary returned no upscaled derivative");

    const blob = await fetchDerived(url, { signal });
    return new File([blob], upscaledFileName(file.name), {
      type: blob.type || "image/png",
    });
  }
}

/**
 * Builds the delivery URL for an eager transformation Cloudinary hasn't
 * returned inline yet.
 *
 * @param {object} params
 * @param {string} [params.transformation] - The signed `eager` string.
 * @param {{ public_id?: string, version?: number }} params.upload
 * @returns {string | null}
 */
function derivedUrl({ transformation, upload }) {
  if (!transformation || !upload?.public_id) return null;

  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/${transformation}/v${upload.version ?? 1}/${upload.public_id}.png`;
}

/**
 * Fetches a Cloudinary derived asset. Cloudinary generates derivations lazily
 * and answers 423 Locked until one is ready, so retry that status with backoff.
 *
 * @param {string} url
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {number} [options.attempts]
 * @returns {Promise<Blob>}
 */
async function fetchDerived(url, { signal, attempts = 6 } = {}) {
  let delay = 1000;

  for (let attempt = 1; ; attempt++) {
    const response = await fetch(url, { signal });
    if (response.ok) return response.blob();

    if (response.status !== 423 || attempt >= attempts) {
      throw new Error(`Cloudinary derived HTTP ${response.status}`);
    }

    await new Promise((resolve) => setTimeout(resolve, delay));
    delay = Math.min(delay * 2, 8000);
  }
}

/**
 * `logo.jpg` → `logo-upscaled.png`. The upscaled derivative is always delivered
 * as PNG, so the original extension would be misleading.
 *
 * @param {string} name
 * @returns {string}
 */
function upscaledFileName(name) {
  return `${name.replace(/\.[^./\\]+$/, "") || "artwork"}-upscaled.png`;
}

/**
 * Reads the guest (logged-out) artwork list from sessionStorage.
 *
 * @returns {Artwork[]}
 */
function readGuestArtworks() {
  try {
    const raw = sessionStorage.getItem(GUEST_ARTWORKS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * Persists the guest artwork list to sessionStorage.
 *
 * @param {Artwork[]} artworks
 */
function writeGuestArtworks(artworks) {
  try {
    sessionStorage.setItem(GUEST_ARTWORKS_KEY, JSON.stringify(artworks));
  } catch {
    // sessionStorage unavailable or over quota — nothing else we can do.
  }
}

/**
 * Uploads a file directly to Google Cloud Storage via a signed URL minted by
 * {@link ArtworksApi#generateGcsUploadUrl}. The signed URL is single-use and
 * the `Content-Type` must match what the backend signed.
 *
 * The artwork's `fileUrl`/`objectName` come from the signed-URL response, not
 * from this call — a successful PUT returns an empty body.
 *
 * @param {File} file
 * @param {object} target - The signed-URL payload from `generateGcsUploadUrl()`.
 * @param {string} target.uploadUrl
 * @param {string} target.contentType
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<void>}
 */
export async function uploadToGcs(
  file,
  { uploadUrl, contentType },
  { signal } = {},
) {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType ?? file.type },
    body: file,
    signal,
  });

  if (!response.ok) throw new Error(`GCS HTTP ${response.status}`);
}
